"use client";

import {
  type EnrichedTweet,
  enrichTweet,
  QuotedTweet,
  TweetActions,
  TweetBody,
  TweetContainer,
  TweetHeader,
  TweetInfo,
  TweetInReplyTo,
  TweetMedia,
  TweetReplies,
} from "react-tweet";
import type { MediaDetails, Tweet } from "react-tweet/api";
import { CustomTweetMediaVideo } from "./custom-tweet-video";
import { normalizeTweetForReactTweet } from "./tweet-normalizer";

type Props = {
  tweet: Tweet;
};

const getMediaKey = (media: MediaDetails): string =>
  `${media.type}-${media.media_url_https}`;

function CustomTweetMedia({ tweet }: { tweet: EnrichedTweet }) {
  const mediaDetails = tweet.mediaDetails;

  if (!mediaDetails?.length) {
    return null;
  }

  const hasVideo = mediaDetails.some(
    (media) => media.type === "video" || media.type === "animated_gif"
  );

  if (!hasVideo) {
    return <TweetMedia tweet={tweet} />;
  }

  return (
    <div className="mt-3 overflow-hidden rounded-xl">
      {mediaDetails.map((media) => {
        const mediaKey = getMediaKey(media);

        if (media.type === "video" || media.type === "animated_gif") {
          return (
            <div className="relative" key={mediaKey}>
              <CustomTweetMediaVideo media={media} tweet={tweet} />
            </div>
          );
        }

        if (media.type === "photo") {
          return (
            <img
              alt={media.ext_alt_text ?? "Tweet media"}
              className="w-full"
              height={media.original_info.height}
              key={mediaKey}
              src={media.media_url_https}
              width={media.original_info.width}
            />
          );
        }

        return null;
      })}
    </div>
  );
}

export function CustomEmbeddedTweetClient({ tweet: t }: Props) {
  const tweet = enrichTweet(normalizeTweetForReactTweet(t));

  return (
    <TweetContainer>
      <TweetHeader tweet={tweet} />
      {tweet.in_reply_to_status_id_str && <TweetInReplyTo tweet={tweet} />}
      <TweetBody tweet={tweet} />
      <CustomTweetMedia tweet={tweet} />
      {tweet.quoted_tweet && <QuotedTweet tweet={tweet.quoted_tweet} />}
      <TweetInfo tweet={tweet} />
      <TweetActions tweet={tweet} />
      <TweetReplies tweet={tweet} />
    </TweetContainer>
  );
}
